import * as React from "react";
import * as PopperJS from "@popperjs/core";
import { usePopper } from 'react-popper';
import styled from "styled-components";
import { CSSTransition } from 'react-transition-group';
import ReactDOM from "react-dom";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { Body1, Body2, Body3 } from "../typography";

// Todo:    Tooltips should not be OS aware and have logic built in for key combinations... I thought this had already been sorted.
//          Onclick and Focus tooltips are not working very well, do we need them. May just want to remove them.

const SHOW_FOR = 10 * 1000;

type TooltipProps = {
    tip: React.ReactNode
    placement?: PopperJS.Placement
    offset?: [number, number]
    delay?: number
    trigger?: "hover" | "focus"
    mobileTrigger?: "focus" | "none"
    strategy?: "absolute" | "fixed"
    disabled?: boolean
    style?: React.CSSProperties
    className?: string
}

export const StyledTooltip = styled.div`
    ${props => props.theme.typography.body2};
    pointer-events: none;
    z-index: ${props => props.theme.zIndex.tooltip};
    border-radius: ${props => props.theme.radius.small};
    background: ${props => props.theme.palette.surfaceContrast};
    color: ${props => props.theme.palette.text.contrast};
    padding: ${props => props.theme.spacing(1, 1)};
    margin: 0;
    box-shadow: ${props => props.theme.shadows.high};
    position:fixed;
    overflow:visible;
    max-width:150px;

	&.fade-appear,
    &.fade-enter {
        opacity: 0;
    }
    &.fade-appear-active,
    &.fade-enter-active,
    &.fade-appear-done,
    &.fade-enter-done {
        opacity: 1;
        transition: opacity ${props => props.theme.transitions.short};
    }
    &.fade-exit {
        opacity: 1;
    }
    &.fade-exit-active,
    &.fade-exit-done {
        opacity: 0;
        transition: opacity ${props => props.theme.transitions.short};
    }

    // Arrow style and positioning
    & > div:first-child {
        position: absolute;
        width: 10px;
        height: 10px;
        z-index: -1;

        :after {
            content: '';
            position: absolute;
            width: 100%;
            height: 100%;
            transform: rotate(45deg);
            background: ${props => props.theme.palette.surfaceContrast};
        }
    }
    &[data-popper-placement^="top"] > div:first-child { bottom: -4px; }
    &[data-popper-placement^="bottom"] > div:first-child { top: -4px; }
    &[data-popper-placement^="left"] > div:first-child { right: -4px; }
    &[data-popper-placement^="right"] > div:first-child { left: -4px; }

    ${Body1},
    ${Body2},
    ${Body3} {
        color: inherit;
    }
`;

const FadeTransition: React.FC<{ in?: boolean }> = (props) => {
    const transitionEnd = (elem: HTMLElement, cb: () => void) => elem.addEventListener('transitionend', cb);
    return <CSSTransition
        classNames="fade"
        addEndListener={transitionEnd}
        unmountOnExit
        {...props}>
    </CSSTransition>
}

const TooltipPortal = (props: { children: React.ReactNode; target?: HTMLElement | undefined; }) => {
    const { children, target = document.body } = props;
    return ReactDOM.createPortal(children, target);
};

const Tooltip: React.FC<TooltipProps> = (props) => {
    const {
        delay = 1000,
        placement = "top",
        trigger = "hover",
        offset = [0, 8],
        strategy, tip, children,
        style, className,
        disabled, mobileTrigger = "none"
    } = props;
    const [open, setOpen] = React.useState(false);
    const timeout = React.useRef<number | null>(null);
    const hold = (ms: number) => new Promise(res => timeout.current = window.setTimeout(res, ms));

    const [targetElem, setTargetElem] = React.useState<HTMLElement | null>(null);
    const [tooltipElem, setTooltipElem] = React.useState<HTMLElement | null>(null);
    const [arrowElem, setArrowElem] = React.useState<HTMLElement | null>(null);
    const [brekapoint] = useBreakpoint()
    const isMobile = brekapoint === "mobile"

    const { styles, attributes } = usePopper(targetElem, tooltipElem, {
        strategy: strategy,
        placement: placement,
        modifiers: [
            {
                name: "arrow",
                enabled: true,
                options: {
                    element: arrowElem,
                    padding: 5
                }
            },
            {
                name: "offset",
                options: {
                    offset: offset
                }
            }
        ]
    });

    // Handlers
    const onMouseEnterHandler = async (e: MouseEvent) => {
        if (timeout.current) clearTimeout(timeout.current);
        await hold(delay);
        setOpen(true);
    };
    const onMouseLeaveHandler = () => {
        if (timeout.current) clearTimeout(timeout.current);
        setOpen(false);
    };
    const onFocusInHandler = async () => {
        if (timeout.current) clearTimeout(timeout.current);
        setOpen(true);
        timeout.current = window.setTimeout(() => {
            setOpen(false);
        }, SHOW_FOR);
    };
    const onFocusOutHandler = () => {
        if (timeout.current) clearTimeout(timeout.current);
        setOpen(false);
    };

    React.useEffect(() => {
        if (!targetElem) return;

        if (isMobile && mobileTrigger == "focus") {
            targetElem.tabIndex = -1;
            targetElem.addEventListener("focusin", onFocusInHandler);
            targetElem.addEventListener("focusout", onFocusOutHandler);

        } else if (!isMobile) {
            switch (trigger) {
                case "hover":
                    targetElem.addEventListener("mouseenter", onMouseEnterHandler);
                    targetElem.addEventListener("mouseleave", onMouseLeaveHandler);
                case "focus":
                    targetElem.addEventListener("focusin", onFocusInHandler);
                    targetElem.addEventListener("focusout", onFocusOutHandler);
            }
        }

        return () => {
            targetElem.removeEventListener("mouseenter", onMouseEnterHandler);
            targetElem.removeEventListener("mouseleave", onMouseLeaveHandler);
            targetElem.removeEventListener("focusin", onFocusInHandler);
            targetElem.removeEventListener("focusout", onFocusOutHandler);
        }
    }, [targetElem]);

    return <>
        <div ref={setTargetElem} style={style} className={className}>
            {children}
        </div>
        {!disabled && tip && <FadeTransition in={open}>
            <TooltipPortal>
                <StyledTooltip ref={setTooltipElem} style={styles.popper} {...attributes.popper}>
                    <div ref={setArrowElem} style={styles.arrow} {...attributes.arrow} />
                    {tip}
                </StyledTooltip>
            </TooltipPortal>
        </FadeTransition> || null}
    </>
}
export default Tooltip;
export { Tooltip, TooltipProps }